import React, { useState } from 'react'
import PropTypes from 'prop-types'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, FormProvider } from 'react-hook-form'
import styled from '@emotion/styled'

import axios from '../../dataClient/dataClient'
import { BoldFormTitle, MedBodyText, PlaceholderSpan } from '../styles/Text'
import { SpaceBetweenRowContainer, WhiteBGContainer, FlexEndButtonErrorContainer, FlexEndContainer, Form } from '../styles/Containers'
import { ErrorMessage } from '../common/UserMessage'
import Button, { SecondaryButton } from '../common/Button'
import InternalSummaryForm from './InternalSummaryForm'
import DetailSection from '../common/DetailSection'

const HeadingContainer = styled(SpaceBetweenRowContainer)`
  align-items: flex-start;
`

const StyledFlexEndContainer = styled(FlexEndContainer)`
  flex-wrap: wrap;
`

const StyledBoldFormTitle = styled(BoldFormTitle)`
  line-height: 36px;
`

yup.addMethod(yup.array, 'unique', function (field, message) {
  return this.test('unique', message, function (arr) {
    if (!arr || arr.length === 0) return true

    let isValid = true
    const checkedValues = new Set()
    const errors = []

    arr.forEach((item, index) => {
      const value = item[field].toLowerCase()
      if (checkedValues.has(value)) {
        isValid = false
        errors.push(this.createError({ path: `${this.path}[${index}].${field}`, message }))
      }

      checkedValues.add(value)
    })

    if (errors.length > 0) throw new yup.ValidationError(errors)

    return isValid
  })
})

const schema = yup.object().shape({
  internalSummary: yup
    .array()
    .of(
      yup.object().shape({
        label: yup.string().required('Label is required'),
        value: yup.string().required('Value is required')
      })
    )
    .unique('label', 'Label must be unique')
})

function InternalSummary({ foiId, internalSummary = [], onUpdateSummary = () => {} }) {
  const [isEditing, setIsEditing] = useState(false)
  const [summary, setSummary] = useState(internalSummary)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: { internalSummary: summary }
  })
  const { reset, handleSubmit } = methods

  const toggleEdit = () => setIsEditing(isEditing => !isEditing)
  const onError = (errors, e) => console.error('Error', errors, e)

  function onSubmit(payload) {
    setLoading(true)

    axios
      .patch(`/foi/${foiId}`, { internalSummary: payload.internalSummary }, { headers: { 'Content-Type': 'application/json' } })
      .then(({ data }) => {
        setSummary(data.data.internalSummary)
        onUpdateSummary(data.data.internalSummary)
        toggleEdit()
        setLoading(false)
      })
      .catch(e => {
        setError(true)
        console.error('Failed to update the internal summary', e)
        setLoading(false)
      })
  }

  function onCancel() {
    reset({ internalSummary: summary })
    toggleEdit()
  }

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit, onError)} gap="20px">
        <HeadingContainer>
          <StyledBoldFormTitle>Internal Summary</StyledBoldFormTitle>
          {!isEditing ? (
            <SecondaryButton onClick={toggleEdit}>Edit</SecondaryButton>
          ) : (
            <FlexEndButtonErrorContainer>
              <StyledFlexEndContainer>
                <SecondaryButton onClick={onCancel} disabled={loading}>
                  Cancel
                </SecondaryButton>
                <Button type="submit" loading={loading}>
                  Save
                </Button>
              </StyledFlexEndContainer>
              {error && <ErrorMessage>Error updating internal summary. Please try again.</ErrorMessage>}
            </FlexEndButtonErrorContainer>
          )}
        </HeadingContainer>
        <WhiteBGContainer padding="15px">
          {isEditing ? (
            <InternalSummaryForm loading={loading} />
          ) : summary.length === 0 ? (
            <MedBodyText>
              <PlaceholderSpan>Add fields to the internal summary by editing</PlaceholderSpan>
            </MedBodyText>
          ) : (
            <DetailSection details={Object.fromEntries(summary.map(obj => [obj.label, obj.value]))} />
          )}
        </WhiteBGContainer>
      </Form>
    </FormProvider>
  )
}

InternalSummary.propTypes = {
  foiId: PropTypes.string.isRequired,
  internalSummary: PropTypes.array,
  onUpdateSummary: PropTypes.func
}

export default InternalSummary
